import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FetchType } from '../../types/api/FetchType';
import { sendLiveRegionMessage } from '../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Namespace } from '../../translation/namespace';
import { FetchError, LazyBlock } from '../../types/api/Block';
import { BlockType, ElementType } from '../../types/api/ElementType';
import { On2ContentBlock } from '../On2Content/On2ContentBlock';
import { ChildPageMetaInfoBlock } from './ChildPageMetaInfoBlock/ChildPageMetaInfoBlock';
import { AttachedFAQFilesBlock } from './FaqAttachedFiles/FaqAttachedFiles';
import { FaqContentWrapperBlock } from './FaqContentWrapper/FaqContentWrapperBlock';
import { isEmptyArray } from '../../lib/typeGuards';
import { IEntity } from '../../types/api/entities/IEntity';

export type AllowedFAQBlocks =
  | LazyBlock<BlockType.on2content>
  | LazyBlock<BlockType.childPageMetaInfo>
  | LazyBlock<BlockType.attachedfiles>
  | LazyBlock<ElementType.contentWrapper>;

export type FetchedFAQBlocks =
  | On2ContentBlock
  | ChildPageMetaInfoBlock
  | AttachedFAQFilesBlock
  | FaqContentWrapperBlock
  | FetchError;

type Entry = {
  id: string;
  content: AllowedFAQBlocks[]; // contains the block data
};

type NotFetchedEntry = IEntity & {
  type: 'notFetched';
  blocks: AllowedFAQBlocks[];
};
const NotFetchedEntry = (id: string, blocks: AllowedFAQBlocks[]): NotFetchedEntry => ({
  id,
  type: 'notFetched',
  blocks,
});

type FetchedEntry = IEntity & {
  type: 'fetched';
  blocks: FetchedFAQBlocks[];
};
const FetchedEntry = (id: string, blocks: FetchedFAQBlocks[]): FetchedEntry => ({ id, type: 'fetched', blocks });

type CacheableEntry = NotFetchedEntry | FetchedEntry;
export function isNotFetched(entry: CacheableEntry): entry is NotFetchedEntry {
  return entry.type === 'notFetched';
}

const initCacheEntries = (allEntries: Entry[]): NotFetchedEntry[] =>
  allEntries.map(({ id, content }) => NotFetchedEntry(id, content));

const instantBlock = (block: AllowedFAQBlocks, embedded: any): FetchedFAQBlocks => ({
  ...block,
  fetchType: FetchType.instant,
  embedded: embedded || null,
});

const fetchError = (block: AllowedFAQBlocks, response: { requestId: string }): FetchError => ({
  ...block,
  ...response,
  fetchType: FetchType.instant,
  fetchError: true,
});

function fetchBlocksInEntry(entry: NotFetchedEntry) {
  return Promise.all(
    entry.blocks.map(block =>
      axios
        .get(block.fetchUrl)
        .then(response => instantBlock(block, response.data))
        .catch(reject => fetchError(block, reject)),
    ),
  );
}

function replaceEntry<T extends IEntity>(entries: T[], replacement: T): T[] {
  return entries.map(entry => (entry.id === replacement.id ? replacement : entry));
}

/**
 * This hook was created to fetch all blocks inside an FAQ List and render only one loading Spinner.
 * - As for now, FAQ blocks are all lazy -> fetch when rendering the block.
 *      -> this led to problems as all Accordion children are rendered instantly -> to massive amounts of request
 *      -> and we had one loading spinner for each lazy block -> bad ux
 */
export function useCacheEntries(entries: Entry[], activeItems: string[]): (NotFetchedEntry | FetchedEntry)[] {
  const { t } = useTranslation(Namespace.phrases);

  const [fetchedEntries, setFetchedEntries] = useState<(NotFetchedEntry | FetchedEntry)[]>(() =>
    initCacheEntries(entries),
  );

  // keeps track of pending entries so we dont can ignore a pending fetch
  const pendingFetches = useRef<string[]>([]);

  useEffect(() => {
    const isOpen = (entry: CacheableEntry) => activeItems.includes(entry.id);
    const addToPendingFetches = (entry: CacheableEntry) => [...pendingFetches.current, entry.id];
    const removeFromPendingFetches = (entry: CacheableEntry) => [...pendingFetches.current, entry.id];
    const isNotPending = (entry: CacheableEntry) => !pendingFetches.current.includes(entry.id);

    const entriesToFetch = fetchedEntries.filter(isOpen).filter(isNotFetched).filter(isNotPending);
    if (isEmptyArray(entriesToFetch)) {
      return;
    }
    // add to list of pending requests
    entriesToFetch.map(addToPendingFetches);

    entriesToFetch.map(entry =>
      fetchBlocksInEntry(entry).then((blocks) => {
        // remove from list of pending requests
        removeFromPendingFetches(entry);
        setFetchedEntries(prev => replaceEntry(prev, FetchedEntry(entry.id, blocks)));
      }),
    );

    sendLiveRegionMessage(t(`contentIsLoading`));
  }, [fetchedEntries, t, activeItems]);

  return fetchedEntries;
}
